import { useQuery } from "@apollo/client/react";
import { ReactNode, useState } from "react";

import { GET_PERSONAL_API_KEYS_COUNT } from "components/ApiKeys/PersonalApiKeys/gql";
import DocumentationIconButton from "components/DocumentationIconButton";
import { Policy, User } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Feedback from "ds/components/Feedback";
import FeedbackActions from "ds/components/Feedback/Actions";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Typography from "ds/components/Typography";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { AuthorizationScheme } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import { AccountContext } from "views/AccountWrapper";
import Link from "ds/components/Link";

import { showChangeManagementStrategyConfirmationModal } from "./ConfirmationModal";
import { GET_AUTHORIZATION_SCHEME } from "./gql";
import { humanizeAuthorizationSchemeName } from "./helpers";
import ManagementStrategyItem from "./Item";

type GetAuthorizationSchemeGql = {
  authorizationScheme: AuthorizationScheme;
};

const DOCS_URL = getDocsUrl(
  "/concepts/identity-access-management/admin#select-your-management-strategy"
);

const SwitchWarningParagraph = ({ children }: { children: ReactNode }) => (
  <Typography tag="p" variant="p-body3">
    {children}
  </Typography>
);

// Shown up front once a different strategy is selected, so the consequences are
// visible before committing rather than only in the confirmation.
const SWITCH_WARNINGS: Record<AuthorizationScheme, ReactNode> = {
  [AuthorizationScheme.ManagedUsers]: (
    <Box direction="column" gap="medium">
      <SwitchWarningParagraph>
        After switching to Identity Access Management, all active sessions (except the current one)
        will be invalidated. Only API keys with root admin access will continue to work.
      </SwitchWarningParagraph>
      <SwitchWarningParagraph>
        You can still edit your existing login policy configuration.
      </SwitchWarningParagraph>
    </Box>
  ),
  [AuthorizationScheme.LoginPolicies]: (
    <Box direction="column" gap="medium">
      <SwitchWarningParagraph>
        After switching to login policy, all active sessions (except the current one) will be
        invalidated. Only API keys with root admin access will continue to work. Non root admin keys
        will need to be defined in the login policy.
      </SwitchWarningParagraph>
      <SwitchWarningParagraph>
        You can still edit your existing Identity Access Management configuration.
      </SwitchWarningParagraph>
    </Box>
  ),
};

const ManagementStrategy = () => {
  const { accountName } = useTypedContext(AccountContext);

  const [selection, setSelection] = useState<AuthorizationScheme>();

  const { personalApiKeys } = useTypedFlags();

  const { loading, data, error } = useQuery<GetAuthorizationSchemeGql>(GET_AUTHORIZATION_SCHEME);
  const { data: countData } = useQuery(GET_PERSONAL_API_KEYS_COUNT, {
    skip: !personalApiKeys || data?.authorizationScheme !== AuthorizationScheme.ManagedUsers,
  });

  useTitle(`Organization Settings · Management strategy · ${accountName}`);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading || !data?.authorizationScheme) {
    return <PageLoading />;
  }

  const activeScheme = data.authorizationScheme;
  const selectedScheme = selection ?? activeScheme;
  const hasPendingChange = selectedScheme !== activeScheme;

  const handleSwitch = () => {
    showChangeManagementStrategyConfirmationModal({
      scheme: selectedScheme,
      name: humanizeAuthorizationSchemeName(selectedScheme),
      keysCount: countData?.personalApiKeysCount ?? 0,
      onSwitched: () => setSelection(undefined),
    });
  };

  return (
    <>
      <PageInfoHeader title="Management strategy">
        <DocumentationIconButton
          href={DOCS_URL}
          tooltipText="Go to management strategy documentation"
        />
      </PageInfoHeader>

      <Feedback type="callout" variant="info">
        Both strategies can be configured at any time, but only one is in effect. Settings for the
        inactive strategy are saved.
      </Feedback>

      <Box direction="column" padding="x-large" gap="x-large" limitWidth="medium">
        <Box direction="row" gap="large">
          <ManagementStrategyItem
            active={activeScheme === AuthorizationScheme.ManagedUsers}
            selected={selectedScheme === AuthorizationScheme.ManagedUsers}
            onSelect={() => setSelection(AuthorizationScheme.ManagedUsers)}
            title="Identity Access Management"
            icon={User}
          >
            Manage users, groups mapped from your IdP, API keys and roles directly in the UI. Access
            is granted by role bindings.
          </ManagementStrategyItem>

          <ManagementStrategyItem
            active={activeScheme === AuthorizationScheme.LoginPolicies}
            selected={selectedScheme === AuthorizationScheme.LoginPolicies}
            onSelect={() => setSelection(AuthorizationScheme.LoginPolicies)}
            title="Login policy"
            icon={Policy}
          >
            A Rego policy decides access at login. Roles are granted from the policy using the roles
            rule, not by role bindings.
          </ManagementStrategyItem>
        </Box>

        {hasPendingChange && (
          <Banner variant="warning">
            {SWITCH_WARNINGS[selectedScheme]}
            <FeedbackActions>
              <Link target="_blank" href={DOCS_URL}>
                Learn more
              </Link>
            </FeedbackActions>
          </Banner>
        )}

        <Box justify="end">
          <Button variant="primary" disabled={!hasPendingChange} onPress={handleSwitch}>
            Switch strategy
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default ManagementStrategy;
